import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { lifetimePreviewSelector } from 'reduxModules/checkout/selectors'
import { SubscriptionPlanPropTypes } from '../../../SubscriptionPlans/SubscriptionPlanList'
import DiscountCode from '../../../DiscountCode'
import getPlanIcon from '../../../utils/getPlanIcon'

import './styles.css'

const formatMoney = (value) => {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return '0.00'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const LifetimePreviewBreakdownBase = ({ preview }) => {
  if (preview.loading)
    return (
      <div className="lifetime-preview lifetime-preview--loading">
        Calculating your upgrade credit…
      </div>
    )
  if (preview.error)
    return (
      <div className="lifetime-preview lifetime-preview--error">
        We couldn&rsquo;t calculate your upgrade credit right now. You&rsquo;ll
        see the final amount on the next page.
      </div>
    )
  const upgradeCredit = parseFloat(preview.upgradeCredit) || 0
  const couponDiscount = parseFloat(preview.couponDiscount) || 0
  const chargeAmount = parseFloat(preview.chargeAmount) || 1199
  return (
    <div className="lifetime-preview">
      <div className="lifetime-preview__row">
        <span>Lifetime plan</span>
        <span>$1,199.00</span>
      </div>
      {couponDiscount > 0 && (
        <div className="lifetime-preview__row lifetime-preview__row--credit">
          <span>Coupon discount</span>
          <span>-${formatMoney(couponDiscount)}</span>
        </div>
      )}
      {upgradeCredit > 0 && (
        <div className="lifetime-preview__row lifetime-preview__row--credit">
          <span>Upgrade credit</span>
          <span>-${formatMoney(upgradeCredit)}</span>
        </div>
      )}
      <div className="lifetime-preview__row lifetime-preview__row--total">
        <span>Total today</span>
        <span>${formatMoney(chargeAmount)}</span>
      </div>
    </div>
  )
}

LifetimePreviewBreakdownBase.propTypes = {
  preview: PropTypes.shape({
    upgradeCredit: PropTypes.string,
    couponDiscount: PropTypes.string,
    chargeAmount: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.any
  }).isRequired
}

const LifetimePreviewBreakdown = connect((state) => ({
  preview: lifetimePreviewSelector(state)
}))(LifetimePreviewBreakdownBase)


export class ChangeSubscriptionConfirmation extends Component {
  // SPEC-088: for Lifetime, Continue stays disabled while the plan is being
  // seeded and the backend preview is loading, so it can't race ahead of
  // the checkout state or the real total.
  syncLifetimeContinue() {
    const { newPlan, preview, changeSubmitButtonStatus } = this.props
    if (!changeSubmitButtonStatus || !newPlan || newPlan.name !== 'Lifetime')
      return
    const loading = Boolean(preview && preview.loading)
    if (loading !== this.lastLoading) {
      this.lastLoading = loading
      changeSubmitButtonStatus(loading)
    }
  }

  constructor(props) {
    super(props)
    this.state = {
      calculatedPrice: this.initialCalculatedPrice(props),
      isProrated: props.isProrated
    }

    this.getResponse = this.getResponse.bind(this)
  }

  initialCalculatedPrice(props) {
    const entry = this.couponPlanEntry(props)
    if (entry && entry.priceDiscount) return entry.priceDiscount
    return props.newPlan.price
  }

  // Only an entry that actually discounts this plan counts: the server sends
  // discount_amount 0 where checkout would refuse the code (plan not
  // discountable and not scoped at it — SPEC-062/063), and a code that does
  // nothing here must neither be summarised nor sent with the upgrade.
  couponPlanEntry(props = this.props) {
    const { preAppliedCoupon, newPlan } = props
    const entry = preAppliedCoupon?.plans?.[newPlan?.id]
    return entry && parseFloat(entry.discountAmount) > 0 ? entry : null
  }

  getNewPlanGraphicPrice() {
    const entry = this.couponPlanEntry()
    if (entry && entry.priceDiscount) return entry.priceDiscount
    return this.props.newPlan.price
  }

  // The plan-transition graphic shows the *monthly* price for both columns —
  // the underlying yearly sticker still appears in the "Total plan cost" block
  // below, so the Mo/Mo framing here lets the user compare apples-to-apples.
  getNewPlanGraphicMonthly() {
    const entry = this.couponPlanEntry()
    if (entry && entry.priceDiscountMonthly) return entry.priceDiscountMonthly
    const { newPlan } = this.props
    return newPlan.priceMonthly || newPlan.price
  }

  componentDidMount() {
    this.syncLifetimeContinue()
  }

  componentDidUpdate(prevProps) {
    this.syncLifetimeContinue()
    const { newPlan, validatedDiscountCode, preAppliedCoupon } = this.props
    let { calculatedPrice } = this.state

    if (preAppliedCoupon) {
      const entry = this.couponPlanEntry()
      if (entry && entry.priceDiscount) calculatedPrice = entry.priceDiscount
      else calculatedPrice = newPlan.price
    } else if (
      validatedDiscountCode &&
      validatedDiscountCode.status === 'DISCOUNT_CODE_SUCCESS'
    )
      calculatedPrice = parseFloat(
        newPlan.price - validatedDiscountCode.validatedCode.discount
      ).toFixed(2)
    else if (
      prevProps.validatedDiscountCode?.status !== validatedDiscountCode?.status
    )
      calculatedPrice = newPlan.price

    if (calculatedPrice !== this.state.calculatedPrice)
      this.setState({ calculatedPrice })
  }

  getResponse() {
    const { preAppliedCoupon, validatedDiscountCode } = this.props
    if (preAppliedCoupon)
      return this.couponPlanEntry()
        ? { codename: preAppliedCoupon.codename }
        : undefined
    return validatedDiscountCode && validatedDiscountCode.validatedCode
  }

  renderCouponSummary() {
    const { preAppliedCoupon, newPlan } = this.props
    const entry = this.couponPlanEntry()
    if (!preAppliedCoupon || !entry) return null
    const yearly = newPlan.rank > 2
    const billed = yearly ? '/year' : '/month'
    const monthly = entry.priceDiscountMonthly
      ? ` ($${entry.priceDiscountMonthly}/mo)`
      : ''
    return (
      <div className="coupon-summary">
        Promo code <strong>{preAppliedCoupon.codename}</strong> applied —
        you&rsquo;ll be charged ${entry.priceDiscount}
        {billed}
        {yearly && monthly} instead of ${newPlan.price}
        {billed}.
        <div className="coupon-sub">
          Discount lasts as long as you remain subscribed.
        </div>
      </div>
    )
  }

  // Translates the backend's opaque upgradeCredit ($X.XX) into a readable
  // "annual price ÷ cycle days = daily rate × days remaining = credit"
  // expression so the user can see how the credit was computed instead of
  // just trusting the number. Falls back to generic copy when we don't have
  // enough data (preview still loading, no credit, or a zero-priced source
  // plan like Group Learning).
  renderUnusedCreditExplanation() {
    const { planInfo, preview } = this.props
    const credit = parseFloat(preview && preview.upgradeCredit)
    const sourcePrice = parseFloat(planInfo && planInfo.price)
    if (!Number.isFinite(credit) || credit <= 0)
      return '- Receive credit for the unused portion of your current plan.'
    if (!Number.isFinite(sourcePrice) || sourcePrice <= 0)
      return '- Receive credit for the unused portion of your current plan.'
    const cycleDays = planInfo.rank > 2 ? 365 : 30
    const cycleLabel = planInfo.rank > 2 ? 'yearly' : 'monthly'
    const dailyRate = sourcePrice / cycleDays
    if (dailyRate <= 0)
      return '- Receive credit for the unused portion of your current plan.'
    const daysRemaining = Math.round(credit / dailyRate)
    if (daysRemaining <= 0)
      return '- Receive credit for the unused portion of your current plan.'
    return `- Receive credit for the unused portion of your current plan: $${sourcePrice.toFixed(2)} ${cycleLabel} ÷ ${cycleDays} days = $${dailyRate.toFixed(2)}/day, × ${daysRemaining} days remaining = $${credit.toFixed(2)}.`
  }

  render() {
    const { props } = this
    const { planInfo, newPlan, preAppliedCoupon } = props
    const isLifetime = newPlan.name === 'Lifetime'
    const showDiscountCodeForm =
      !isLifetime &&
      !(preAppliedCoupon && this.couponPlanEntry()) &&
      props.discountCode &&
      (!props.validatedDiscountCode ||
        (props.validatedDiscountCode &&
          props.validatedDiscountCode.status !== 'DISCOUNT_CODE_SUCCESS'))
    return (
      <div>
        <div className="upgrade-graphic">
          <div className="current-plan">
            <i className={getPlanIcon(planInfo.name)} />
            <span className="name">{planInfo.name}</span>
            <span>${planInfo.priceMonthly || planInfo.price}/Mo</span>
          </div>
          <div className="arrow">➔</div>
          <div className="new-plan">
            {isLifetime ? (
              <span aria-hidden="true" className="lifetime-star">★</span>
            ) : (
              <i className={getPlanIcon(newPlan.name)} />
            )}
            <span className="name">{newPlan.name}</span>
            <span>
              {isLifetime
                ? '$1,199 one-time'
                : `$${this.getNewPlanGraphicMonthly()}/Mo`}
            </span>
          </div>
        </div>
        {isLifetime && (
          <div>
            <div className="message">
              You are about to upgrade to the Lifetime plan. With a one-time payment, you&rsquo;ll unlock unlimited lifetime access — no more recurring charges.
            </div>
            <div className="message">By upgrading now you will:</div>
            <div className="benefit">- Make a one-time payment (PayPal Pay Later available).</div>
            <div className="benefit">- Replace your current {planInfo.name} plan with Lifetime access.</div>
            <div className="benefit">{this.renderUnusedCreditExplanation()}</div>
            <LifetimePreviewBreakdown />
            <div className="totalTagline">
              You&rsquo;ll be taken to checkout to complete your purchase.
            </div>
          </div>
        )}
        {!isLifetime && this.state.isProrated && (
          <div>
            <div className="message">
              You are about to upgrade to a superior plan. As an upgrading subscriber, you get a Reward Discount on your first payment cycle * based on the cost of your current plan and on the time left for its expiration.
            </div>
            <div className="disclaimer">
              * Only the first payment cycle will include a Reward Discount. It does not apply any price cuts for next payment cycles.
            </div>
            <div className="total">Total cost of the first payment cycle with Subscriber Discount:</div>
            <div className="proratedValue">${newPlan.firstMonthPrice}/{newPlan.rank > 2 ? 'Yr' : 'Mo'}</div>
            <div className="totalTagline">
              Next payments cycles will be ${newPlan.regularPrice}/{newPlan.rank > 2 ? 'Yearly' : 'Monthly'}
            </div>
          </div>
        )}
        {!isLifetime && !this.state.isProrated && (
          <div>
            <div className="message">
              You are about to upgrade your subscription plan. If you decide to upgrade now, you will start enjoying all the subscriber benefits from the {newPlan.name} plan immediately.
            </div>
            <div className="message">By upgrading now you will:</div>
            <div className="benefit">- Pay for the {newPlan.name} plan.</div>
            <div className="benefit">- Start enjoying all the {newPlan.name} subscriber benefits.</div>
            <div className="total">Total plan cost:</div>
            <div className="newValue">
              $<span id="realValue">{this.state.calculatedPrice}</span>
              {`/${newPlan.rank > 2 ? 'Yr' : 'Mo'}`}
            </div>
          </div>
        )}
        {this.renderCouponSummary()}
        {showDiscountCodeForm && (
          <div className="discount-code">
            {!props.displayForm && (
              <a className="subscribe" onClick={() => props.toggleDiscountCode()}>
                Redeem a discount code
              </a>
            )}
            <DiscountCode className="change-subscription" plan={props.newPlan} />
          </div>
        )}
      </div>
    )
  }
}

ChangeSubscriptionConfirmation.propTypes = {
  validatedCode: PropTypes.shape({
    codename: PropTypes.string,
    discount: PropTypes.number,
    status: PropTypes.string
  }),
  newPlan: PropTypes.shape(SubscriptionPlanPropTypes).isRequired,
  planInfo: PropTypes.shape(SubscriptionPlanPropTypes).isRequired,
  preAppliedCoupon: PropTypes.shape({
    codename: PropTypes.string,
    discount: PropTypes.number,
    discountType: PropTypes.string,
    plan: PropTypes.number,
    plans: PropTypes.object
  }),
  changeSubmitButtonStatus: PropTypes.func,
  preview: PropTypes.shape({
    upgradeCredit: PropTypes.string
  })
}

ChangeSubscriptionConfirmation.defaultProps = {
  validatedCode: {},
  preAppliedCoupon: null,
  changeSubmitButtonStatus: null,
  preview: { upgradeCredit: '0.00' }
}

export default connect((state) => ({
  preview: lifetimePreviewSelector(state)
}))(ChangeSubscriptionConfirmation)
